import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search, ArrowUp, Bookmark, BookmarkCheck, Share2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { shareReportView, useReportHeadInToolbar } from './ReportPageShell';
import { DateRangePicker } from '../DateRangePicker';
import './ReportsNative.css';

/* ------------------------------------------------------------------ */
/* ReportSegmented — pill segmented control per the report mocks.      */
/* Same props contract as components/ui/SegmentedControl (options/     */
/* value/onChange) but styled to the report filter-bar spec: pill      */
/* track on onyx-50, orange active segment with glow.                  */
/* ------------------------------------------------------------------ */

export function ReportSegmented({ options = [], value, onChange, size = 'md', ariaLabel, className }) {
  return (
    <div
      className={`rn-seg${size === 'sm' ? ' rn-seg-sm' : ''}${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={active ? 'rn-active' : undefined}
            onClick={() => onChange?.(opt.key)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FilterChip — labeled dropdown chip with popover option list.        */
/* ------------------------------------------------------------------ */

function computeDisplay({ value, options, multiple, allLabel }) {
  if (multiple) {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0 || (options && arr.length === options.length)) return allLabel;
    if (arr.length === 1) {
      const opt = options?.find((o) => o.value === arr[0]);
      return opt ? opt.label : allLabel;
    }
    return `${arr.length} selected`;
  }
  if (value === undefined || value === null || value === '') return allLabel;
  const opt = options?.find((o) => o.value === value);
  return opt ? opt.label : String(value);
}

/**
 * Labeled filter chip ("LOCATIONS  All locations ⌄") with a popover
 * option list. Single- or multi-select.
 *
 * Props:
 *   label         small uppercase label inside the chip ('Locations')
 *   options       [{ value, label, disabled?, title?, group? }] — consecutive
 *                 options sharing a `group` render under that heading
 *   value         scalar (single) or array (multiple)
 *   onChange      (nextValue) => void — array when multiple
 *   multiple      checkbox-style multi-select (default false)
 *   allLabel      display + clear-row text when nothing selected (default 'All')
 *   displayValue  override the computed chip text
 *   searchable    show a search input (default: options.length > 10)
 *   count         optional mono count badge ("More filters ②")
 *   disabled, align ('left'|'right' popover alignment)
 */
export function FilterChip({
  label,
  options = [],
  value,
  onChange,
  multiple = false,
  allLabel = 'All',
  displayValue,
  searchable,
  count,
  disabled = false,
  align = 'left',
  clearable = true,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const showSearch = searchable ?? options.length > 10;
  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query]);

  const selectedSet = useMemo(
    () => new Set(multiple ? (Array.isArray(value) ? value : []) : value !== undefined && value !== null ? [value] : []),
    [value, multiple]
  );

  const handleSelect = (optValue) => {
    if (multiple) {
      const arr = Array.isArray(value) ? [...value] : [];
      const idx = arr.indexOf(optValue);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(optValue);
      onChange?.(arr);
    } else {
      onChange?.(optValue);
      setOpen(false);
    }
  };

  const handleClear = () => {
    onChange?.(multiple ? [] : null);
    if (!multiple) setOpen(false);
  };

  const display = displayValue ?? computeDisplay({ value, options, multiple, allLabel });
  const cleared = multiple ? selectedSet.size === 0 : value === undefined || value === null || value === '';

  return (
    <div className={`rn-chip-wrap${className ? ` ${className}` : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`rn-filter-chip${open ? ' rn-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label && <span className="rn-chip-lbl">{label}</span>}
        {display}
        {count != null && <span className="rn-chip-count">{count}</span>}
        <ChevronDown />
      </button>
      {open && (
        <div className={`rn-popover${align === 'right' ? ' rn-popover-right' : ''}`} role="listbox" aria-multiselectable={multiple}>
          {showSearch && (
            <div className="rn-popover-search">
              <Search />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                aria-label={`Search ${label || 'options'}`}
              />
            </div>
          )}
          <div className="rn-popover-list">
            {clearable && (
              <>
                <button
                  type="button"
                  className={`rn-popover-option${cleared ? ' rn-selected' : ''}`}
                  role="option"
                  aria-selected={cleared}
                  onClick={handleClear}
                >
                  <Check className="rn-opt-check" />
                  {allLabel}
                </button>
                <div className="rn-popover-divider" />
              </>
            )}
            {filtered.length === 0 && <div className="rn-popover-empty">No matches</div>}
            {filtered.map((opt, i) => {
              const selected = selectedSet.has(opt.value);
              const heading = opt.group && opt.group !== filtered[i - 1]?.group
                ? <div className="rn-popover-group" role="presentation">{opt.group}</div>
                : null;
              return (
                <React.Fragment key={String(opt.value)}>
                {heading}
                <button
                  key={String(opt.value)}
                  type="button"
                  className={`rn-popover-option${selected ? ' rn-selected' : ''}`}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={opt.disabled || undefined}
                  disabled={opt.disabled}
                  title={opt.title}
                  onClick={() => handleSelect(opt.value)}
                >
                  <Check className="rn-opt-check" />
                  {opt.label}
                </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ReportFilterBar                                                     */
/* ------------------------------------------------------------------ */

const DEFAULT_GRANULARITY = [
  { key: 'day', label: 'Day' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

/**
 * Page toolbar (all native reports + the dashboard). Flat, full-bleed to the
 * shell card, sticky under the shell header; gains a soft shadow while
 * parked. Its height is published as `--rn-toolbar-h` so other sticky page
 * elements (e.g. the Revenue workspace rail) can park beneath it.
 *
 * Props:
 *   periods            [{ key, label }] period presets for the segmented control
 *   period             active preset key
 *   onPeriodChange     (key) => void
 *   customPeriodKey    preset key that reveals the DateRangePicker (default
 *                      'custom'); null = picking dates doesn't touch the period
 *   dateRange          { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } for custom range
 *   onDateRangeChange  ({ start, end }) => void
 *   minDate, maxDate   passed through to DateRangePicker
 *   datePlaceholder    DateRangePicker placeholder
 *   granularity        active D/M/Y key — granularity control renders only
 *                      when onGranularityChange is provided
 *   granularityOptions default Day/Month/Year
 *   onGranularityChange(key) => void
 *   filters            [{ key, label, options, value, onChange, multiple,
 *                         allLabel, displayValue, searchable, count,
 *                         clearable, align }] — each rendered as a FilterChip
 *   periodDefault      { isDefault, saveDefault, title? } — bookmark control
 *   sticky             park under the shell header (default: true in the
 *                      inset shell, false in the classic shell)
 *   dock               classic shell only: bottom-centre floating pill bar
 *                      that appears when the bar scrolls out of view
 *                      (default true; never rendered in the inset shell)
 *   left, children     extra nodes (left renders first; children render
 *                      after the chips, before the spacer)
 *   right              nodes pinned to the right edge (after the spacer)
 */
export function ReportFilterBar({
  periods,
  period,
  onPeriodChange,
  customPeriodKey = 'custom',
  dateRange,
  onDateRangeChange,
  minDate,
  maxDate,
  datePlaceholder,
  granularity,
  granularityOptions = DEFAULT_GRANULARITY,
  onGranularityChange,
  filters = [],
  periodDefault,
  sticky: stickyProp,
  dock = true,
  left,
  right,
  children,
  className,
  style,
}) {
  const barRef = useRef(null);
  const { insetActive: inset } = useTheme();
  const head = useReportHeadInToolbar();
  const sticky = stickyProp ?? inset;
  const showDock = !inset && dock && !sticky;
  const [stuck, setStuck] = useState(false);
  const [dockVisible, setDockVisible] = useState(false);
  // Imperative calendar open: clicking the "Custom" segment opens the
  // calendar immediately (no second click on the date chip)
  const [calendarOpenKey, setCalendarOpenKey] = useState(0);

  const handlePeriodSelect = (key) => {
    if (key === customPeriodKey && onDateRangeChange) {
      setCalendarOpenKey((k) => k + 1);
    }
    onPeriodChange?.(key);
  };

  // Picking dates in the always-visible calendar switches the period to
  // Custom in the same gesture (period first, so the page's custom-seed
  // doesn't overwrite the freshly picked range)
  const handleDatePicked = (r) => {
    if (customPeriodKey != null) onPeriodChange?.(customPeriodKey);
    onDateRangeChange?.(r);
  };

  // Stuck state: the bar's top has reached its sticky offset and the page
  // has scrolled. rAF-throttled window scroll (the document scrolls).
  useEffect(() => {
    if (!sticky) return undefined;
    let frame = null;
    const update = () => {
      const el = barRef.current;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky') {
        setStuck(false);
        return;
      }
      const offset = parseFloat(cs.top) || 0;
      setStuck(window.scrollY > 0 && el.getBoundingClientRect().top <= offset + 0.5);
    };
    const request = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    request();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
    };
  }, [sticky]);

  // Classic shell: show the floating dock once the bar has scrolled off the
  // top (same rAF-throttled scroll tracking as the classic dashboard).
  useEffect(() => {
    if (!showDock) return undefined;
    let frame = null;
    const update = () => {
      const el = barRef.current;
      if (!el) return;
      setDockVisible(el.getBoundingClientRect().bottom < 12);
    };
    const request = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    request();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
    };
  }, [showDock]);

  // Publish the toolbar height for other sticky elements.
  useEffect(() => {
    const el = barRef.current;
    if (!sticky || !el || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement.style;
    const ro = new ResizeObserver(() => {
      root.setProperty('--rn-toolbar-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.removeProperty('--rn-toolbar-h');
    };
  }, [sticky]);

  const renderChips = () =>
    filters.map((f) => (
      <FilterChip
        key={f.key}
        label={f.label}
        options={f.options}
        value={f.value}
        onChange={f.onChange}
        multiple={f.multiple}
        allLabel={f.allLabel}
        displayValue={f.displayValue}
        searchable={f.searchable}
        count={f.count}
        disabled={f.disabled}
        align={f.align}
        clearable={f.clearable}
      />
    ));
  const periodOptions = useMemo(
    () => (periods || []).map((p) => ({ value: p.key, label: p.label })),
    [periods]
  );
  const granOptions = useMemo(
    () => (granularityOptions || []).map((g) => ({ value: g.key, label: g.label })),
    [granularityOptions]
  );

  return (
    <>
    <div
      ref={barRef}
      role="toolbar"
      aria-label="Report filters"
      className={`rn-filter-bar${sticky ? ' rn-sticky' : ''}${stuck ? ' rn-stuck' : ''}${head ? ' rn-tb-flush' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      {head?.tabs ? (
        <>
          <div className="rn-tb-tabs">{head.tabs}</div>
          <span className="rn-tb-sep" aria-hidden="true" />
        </>
      ) : null}
      {left}
      {onGranularityChange && (
        <ReportSegmented
          options={granularityOptions}
          value={granularity}
          onChange={onGranularityChange}
          ariaLabel="Trend granularity"
        />
      )}
      {periods && periods.length > 0 && (
        <ReportSegmented options={periods} value={period} onChange={handlePeriodSelect} ariaLabel="Period preset" />
      )}
      {onDateRangeChange && (
        <DateRangePicker
          startDate={dateRange?.start}
          endDate={dateRange?.end}
          onDateChange={handleDatePicked}
          minDate={minDate}
          maxDate={maxDate}
          placeholder={datePlaceholder}
          openKey={calendarOpenKey}
        />
      )}
      {periodDefault && (
        <button
          type="button"
          className={`rn-period-save${periodDefault.isDefault ? ' rn-period-save-on' : ''}`}
          title={periodDefault.title || (periodDefault.isDefault
            ? 'This is your saved default range for this report'
            : 'Save the current range as my default for this report')}
          disabled={periodDefault.isDefault || periodDefault.saving}
          onClick={() => periodDefault.saveDefault?.()}
        >
          {periodDefault.isDefault ? <BookmarkCheck /> : <Bookmark />}
          {periodDefault.isDefault ? 'Default' : 'Set default'}
        </button>
      )}
      {renderChips()}
      {children}
      <div className="rn-spacer" />
      {right}
      {head ? (
        <>
          {/* the aside (dashboard briefing) takes the context's room; the breadcrumb names the page */}
          {!head.aside && (head.title || head.subtitle) && (
            <span className="rn-tb-context" title={typeof head.subtitle === 'string' ? `${head.title} · ${head.subtitle}` : undefined}>
              {head.title && <b>{head.title}</b>}
              {head.title && head.subtitle ? <span className="rn-tb-dot">·</span> : null}
              {head.subtitle}
            </span>
          )}
          {head.chips}
          {head.aside ? <div className="rn-tb-aside">{head.aside}</div> : null}
        </>
      ) : null}
    </div>

      {showDock && (
        <div
          className={`rn-float-dock${dockVisible ? ' rn-visible' : ''}`}
          role="toolbar"
          aria-label="Quick filters"
        >
          {periods && periods.length > 0 && (
            <FilterChip
              label="Period"
              options={periodOptions}
              value={period}
              onChange={handlePeriodSelect}
              clearable={false}
            />
          )}
          {onDateRangeChange && (
            /* DateRangePicker portals its calendar to <body> with fixed
               positioning and flips upward near the viewport bottom, so it
               escapes the dock's overflow clipping on its own */
            <DateRangePicker
              startDate={dateRange?.start}
              endDate={dateRange?.end}
              onDateChange={handleDatePicked}
              minDate={minDate}
              maxDate={maxDate}
            />
          )}
          {onGranularityChange && (
            <FilterChip
              label="View"
              options={granOptions}
              value={granularity}
              onChange={onGranularityChange}
              clearable={false}
            />
          )}
          {(filters.length > 0 || children) && <span className="rn-float-sep" />}
          {renderChips()}
          {children}
          <span className="rn-float-sep" />
          <button
            type="button"
            className="rn-float-top rn-float-share"
            title="Share this exact view — the link keeps filters, drill-downs and your scroll position"
            aria-label="Share this view"
            onClick={() => shareReportView()}
          >
            <Share2 />
          </button>
          <button
            type="button"
            className="rn-float-top"
            title="Back to top"
            aria-label="Scroll back to the filter bar"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <ArrowUp />
          </button>
        </div>
      )}
    </>
  );
}

export default ReportFilterBar;
